import storeModule from './storeModule';
import store from '@/store'
import {initialAbility} from '@/libs/acl/config';
import ability from '@/libs/acl/ability';
import {canNavigate, isRouteBlockedByPlan, isRouteBlockedByPlanAsync} from '@/libs/acl/routeProtection'
import {baseRoute} from '@/velta/utils/baseRoute';
import {lerUltimaRota, limparUltimaRota} from '@/velta/utils/ultimaRota';
import Vue from 'vue';
import router from '@/router';

// Importação dinâmica para evitar dependência circular
const getPlanPermissionsModule = () => import('@/libs/acl/planPermissions');

const showPlanBlockSwal = (planBlockInfo) => {
    Vue.prototype.$swal({
        title: 'Funcionalidade não disponível',
        html: `
            <div class="text-center">
                <p class="mb-1">A funcionalidade <strong>"${planBlockInfo.resourceName}"</strong> não está disponível no seu plano atual.</p>
                <p class="text-muted mb-0">Faça upgrade para o plano <strong>${planBlockInfo.requiredPlan}</strong> ou superior para desbloquear este recurso.</p>
            </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Ver Planos',
        cancelButtonText: 'Fechar',
        customClass: {
            confirmButton: 'btn btn-primary mr-2',
            cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
    }).then((result) => {
        if (result.isConfirmed) {
            router.push({ name: 'planosepagamentos' })
        }
    })
}

// Telas de login da área da empresa onde o boot frio do PWA pode cair: o
// `start_url` do manifest aponta para `login-geral`, e de lá o usuário toca em
// "Sou Prestador" e vai para `login-company`. A restauração fica restrita a
// essas duas para não interferir em telas públicas legítimas (cadastro de
// empresa, blog, termo de uso), onde arrastar o usuário para dentro do sistema
// seria um efeito colateral indesejado.
//
// TRADE-OFF CONSCIENTE (não "conserte" de volta): os links "Login geral" do
// site institucional apontam para `login-geral`, a mesma rota do `start_url` do
// manifest. Com sessão de empresa viva, clicar neles agora leva para dentro do
// sistema em vez de mostrar o formulário — a mesma rota não pode fazer as duas
// coisas. Restaurar é a metade certa: o comportamento antigo destruía em
// silêncio uma sessão válida, que é exatamente o bug corrigido aqui. Para
// trocar de operador existem o botão "Sair" e o `?sair=1`.
const ROTAS_COM_RESTAURACAO = ['login-geral', 'login-company']

// Telas públicas que NÃO podem encerrar uma sessão só por serem abertas.
//
// O ramo de logout logo abaixo existe para o caso "cheguei na tela de login,
// logo quero trocar de conta". Estas duas não são isso:
//
// - `selecionar-perfil` roda no MEIO do login, quando ainda não há sessão, e o
//   passo seguinte é criar uma. Cair no logout desfaria a autenticação que a
//   própria tela acabou de pedir.
// - `convite-vinculo` é aberta pelo link de um e-mail, com frequência no mesmo
//   aparelho em que a pessoa já está trabalhando. Confirmar um acesso não é
//   motivo para derrubá-la do sistema.
//
// `definir-senha` fica de fora desta lista de propósito: trocar a senha é
// justamente um bom momento para encerrar as sessões antigas.
const ROTAS_SEM_LOGOUT = ['selecionar-perfil', 'convite-vinculo']

// Único lugar que define "saída de propósito" pela query. Compartilhado entre o
// guard e `podeRestaurarNaEscolhaDePerfil` para as duas pontas não divergirem —
// já estiveram escritas de dois jeitos (`=== '1'` no guard e "existe" na tela),
// que discordam em `?sair=0`. `String(...)` para que `query: { sair: 1 }`
// numérico também valha.
const ehSaidaExplicita = (route) => String(route?.query?.sair) === '1'

// Tela padrão quando não há última rota utilizável. `home-company` declara
// `resource: "any"`, que toda sessão autenticada possui — é o mesmo destino do
// login normal, então nunca esbarra em "acesso negado" logo após restaurar.
const ROTA_PADRAO_EMPRESA = 'home-company'

// Rede móvel que conecta mas não responde deixaria o app parado no splash:
// a tela de login precisa pintar mesmo sem resposta. Restaurar é conveniência.
// A instância do axios não define `timeout` (0 = infinito), então o limite
// precisa vir daqui.
const RESTAURACAO_TIMEOUT_MS = 4000

// Marcador de identidade única para saber que quem venceu a corrida foi o
// relógio — o dispatch resolve `undefined`, que não serve de sinal.
const TEMPO_ESGOTADO = Object.freeze({ restauracao: 'timeout' })

// Registra que o `fetchAuthUser` DA EMPRESA chegou ao fim ao menos uma vez
// nesta carga da página. Tem dois usos: a tela de escolha de perfil
// (`LoginGeral.vue`) não repete a consulta que o guard acabou de fazer ("já
// consultado e sem sessão" é resposta definitiva, então ela pinta na hora, sem
// spinner e sem uma segunda requisição), e é ele que diz que o `userData` na
// memória veio da empresa e não da área do cliente. Só vira `true` quando a
// resposta realmente chegou (timeout não conta) ou num logout intencional; o
// pior caso de estar errado é não restaurar — o comportamento antigo, nunca uma
// restauração indevida.
let sessaoConsultadaNoServidor = false

// Consulta em voo. Quando o teto de tempo acima estoura, a requisição continua
// correndo: quem perguntar em seguida (o `LoginGeral.vue`, que monta logo
// depois do guard desistir) reaproveita ESTA promessa em vez de disparar uma
// segunda igual numa rede que já está ruim.
//
// É um memo de deduplicação e nada mais: zerar esta variável NÃO aborta a
// requisição (não há `AbortController` aqui), só perde o reaproveitamento. Quem
// já estava esperando a promessa continua esperando, e o `.then` dela ainda vai
// rodar e gravar na store. Dois lugares zeram o memo, por motivos diferentes: o
// `.then` abaixo (a resposta chegou) e `marcarSessaoEncerrada` (a sessão foi
// encerrada de propósito).
let consultaEmVoo = null

// `fetchAuthUser` trata a falha internamente (esvazia o `userData` e não
// rejeita); o `.catch` é só cinto de segurança para não vazar uma rejeição não
// tratada. Quem decide de verdade é o getter `authenticated`.
const consultarSessaoUmaVezSo = () => {
    if (!consultaEmVoo) {
        consultaEmVoo = store.dispatch('auth/fetchAuthUser')
            .catch(() => null)
            .then((resposta) => {
                // A resposta chegou (com ou sem sessão), mesmo que quem pediu
                // já tenha desistido pelo tempo. Libera a próxima consulta.
                consultaEmVoo = null
                sessaoConsultadaNoServidor = true
                return resposta
            })
    }

    return consultaEmVoo
}

// Chamado depois de um logout intencional, em QUALQUER das duas áreas: o
// marcador vale para a carga da página inteira, não só para a empresa. Um
// logout de cliente marcando a sessão da empresa como "já consultei, não há" é
// conservador na direção certa — no máximo deixa de restaurar.
//
// Efeito: `podeRestaurarNaEscolhaDePerfil` passa a devolver `false` até a
// próxima navegação com recarga completa, então a escolha de perfil pinta na
// hora, sem perguntar ao servidor de novo.
//
// RESÍDUO CONHECIDO: zerar `consultaEmVoo` não cancela nada (ver comentário lá
// em cima). Se houvesse um `fetchAuthUser` em voo neste exato instante, o
// `.then` dele ainda gravaria o `userData` e voltaria a marcar a sessão como
// consultada, derrotando este marcador. Não há caminho vivo que faça isso hoje
// (todo chamador de `consultarSessaoUmaVezSo` está a montante de um `next()`
// que já terminou quando um logout roda), mas o marcador é uma barreira a
// mais, não uma garantia.
const marcarSessaoEncerrada = () => {
    consultaEmVoo = null
    sessaoConsultadaNoServidor = true
}

// Pergunta ao servidor se o cookie de sessão da empresa ainda vale, com teto de
// tempo. `true` = há sessão viva e a store está preenchida.
const confirmarSessaoNoServidor = async () => {
    // Atalho para a store já quente: a consulta do guard chegou depois do teto
    // de tempo dele e preencheu o `userData`. Não há o que perguntar de novo.
    //
    // A conjunção com `sessaoConsultadaNoServidor` NÃO é enfeite: `userData` é
    // compartilhado com a área do cliente (`fetchAuthUserCliente` grava no
    // mesmo lugar), então `authenticated` sozinho não diz de qual área é a
    // sessão. Sem ela, um `userData` de cliente satisfaria o atalho e o
    // `ability.update` do chamador rodaria com permissões de cliente para
    // navegar dentro da área da empresa.
    if (sessaoConsultadaNoServidor && store.getters['auth/authenticated']) return true

    let temporizador = null

    const resultado = await Promise.race([
        consultarSessaoUmaVezSo(),
        new Promise((resolve) => {
            temporizador = setTimeout(() => resolve(TEMPO_ESGOTADO), RESTAURACAO_TIMEOUT_MS)
        }),
    ])

    clearTimeout(temporizador)

    // Estourou o tempo: seguimos para a tela de login. A requisição pendente
    // não tem como agir sozinha depois — ela só preenche o `userData`; quem
    // navega e quem chama `ability.update` é sempre quem chamou esta função.
    // No máximo o `userData` chega com o usuário já na tela de escolha, e aí o
    // atalho lá em cima devolve `true` sem uma segunda requisição.
    if (resultado === TEMPO_ESGOTADO) return false

    return store.getters['auth/authenticated']
}

// Núcleo da restauração de sessão, COMPARTILHADO entre o guard de rota e a tela
// de escolha de perfil (`LoginGeral.vue`). Vive aqui, e não duplicado nos dois
// lugares, porque as regras delicadas (teto de tempo, `ability.update` antes do
// `canNavigate`, validação da rota gravada) precisam mudar juntas nos dois.
// Devolve o destino para onde o usuário deve ir, ou `null` se não há sessão.
//
// Fora a área, não checa nada sobre a rota atual: se restaurar é desejável
// (saída explícita, `?r`, primeira navegação) quem decide é o chamador — no
// guard, `restaurarSessaoSeExistir`; na tela de escolha de perfil,
// `podeRestaurarNaEscolhaDePerfil`.
const resolverDestinoSessaoAtiva = async () => {
    // Restaurar é exclusivo da área da empresa — só existe `fetchAuthUser`
    // (empresa) aqui dentro. Trava para um chamador futuro montado sob
    // `/sistema/cliente`; é redundante no guard (`restaurarSessaoSeExistir` já
    // checa) e NÃO cobre o `LoginGeral.vue`, que vive sob `/sistema/empresa` e
    // portanto sempre passa por aqui. Lá quem separa as duas áreas é a
    // conjunção em `confirmarSessaoNoServidor`.
    if (baseRoute() !== 'empresa') return null

    if (!(await confirmarSessaoNoServidor())) return null

    ability.update(store.getters['auth/abilities'])

    const ultima = lerUltimaRota()

    if (ultima) {
        const destino = { name: ultima.name, params: ultima.params, query: ultima.query }

        // Contrato do `ultimaRota.js`: o nome gravado sobrevive a deploys e pode
        // não existir mais. Como nenhum arquivo de rotas tem rota coringa, um
        // nome inexistente resolve para `matched: []`, o que faz o `canNavigate`
        // do fim deste guard reprovar e despejar o usuário em "acesso negado" —
        // beco sem saída logo depois de abrir o app. Mesma coisa para uma tela
        // que o perfil perdeu. Nos dois casos é melhor limpar a chave podre e
        // abrir a tela padrão.
        let resolvida = null

        try {
            resolvida = router.resolve(destino).route
        } catch (e) {
            resolvida = null
        }

        if (resolvida && resolvida.matched.length > 0 && canNavigate(resolvida)) {
            return destino
        }

        limparUltimaRota()
    }

    return { name: ROTA_PADRAO_EMPRESA }
}

// Boot frio do PWA/aba descartada: `authenticated` lê `userData` da memória,
// que está vazia, mas o cookie de sessão pode continuar válido. Confere no
// servidor e, havendo sessão, devolve o destino para onde o usuário estava.
const restaurarSessaoSeExistir = async (to, from, base) => {
    if (base !== 'empresa') return null
    if (ROTAS_COM_RESTAURACAO.indexOf(to.name) === -1) return null
    if (to.query?.r) return null   // login com retorno explícito: respeitar

    // Só faz sentido na PRIMEIRA navegação do app (boot frio): aí `from` é a
    // rota inicial do vue-router, sem nome. Navegação já dentro do app tem a
    // store quente — se ali `authenticated` é falso, o usuário está mesmo
    // deslogado, e consultar o servidor de novo só atrasaria a tela de login.
    // O que sobra desta janela (chegar no `login-geral` por navegação interna
    // com a store fria) é coberto pelo `onMounted` do próprio `LoginGeral.vue`.
    if (from && from.name) return null

    return resolverDestinoSessaoAtiva()
}

// Decisão SÍNCRONA de `LoginGeral.vue`: vale a pena perguntar ao servidor ao
// montar? Mora aqui, junto do guard, para não existirem duas políticas de
// restauração que possam divergir — em especial a leitura de `?sair`.
//
// `false` = pinta a escolha de perfil na hora, sem spinner e sem requisição.
const podeRestaurarNaEscolhaDePerfil = (route) => {
    // Saída de propósito ("Sair"/"Limpar cache" da navbar e o push do
    // interceptor de 422): o guard acabou de encerrar a sessão. Perguntar de
    // novo aqui poderia devolver para dentro do sistema justamente quem pediu
    // para sair.
    if (ehSaidaExplicita(route)) return false

    // Login com retorno explícito: quem montou a URL quer o formulário.
    if (route?.query?.r) return false

    // O servidor já foi consultado nesta carga da página (pelo guard, ou por um
    // logout intencional). Se não há sessão da empresa, não há o que restaurar.
    if (sessaoConsultadaNoServidor && !store.getters['auth/authenticated']) return false

    return true
}

/**
 * Para onde a empresa precisa ir antes de usar o sistema: onboarding, WhatsApp
 * de suporte, ou nenhum (null).
 *
 * Existe como função porque precisa rodar nos DOIS caminhos do guard — store
 * quente (navegação dentro do app) e store fria (recarga completa). Enquanto
 * ficou só no caminho quente, quem acabava de entrar caía na Home: desde o
 * login único, entrar é sempre uma navegação de página inteira (`baseRoute()`
 * é lido do path no load do módulo), então a store chega vazia e o onboarding
 * era pulado justamente na hora em que mais importa.
 */
const redirecionoDeConfiguracaoInicial = (to) => {
    const empresaData = store.getters['auth/empresaData']
    const tenantConfig = store.getters['auth/tenantConfig'] || {}

    const routeOnboarding = ['onboarding', 'initialConfiguration'].indexOf(to.name) !== -1

    // Verificar se o onboarding foi completado (novo campo)
    const onboardingCompleted = tenantConfig && tenantConfig.onboarding_completed

    // Verificar configurações iniciais antigas (fallback)
    const faltaFazerConfiguracoesIniciais = (!tenantConfig.conf_configuracoes || !tenantConfig.conf_servicos || !tenantConfig.conf_vagas)

    // Wizard começado e não terminado. Os `conf_*` não servem para detectar
    // isso: o próprio wizard os liga passo a passo, então quem para no meio
    // fica idêntico a uma empresa antiga que já tinha configurado tudo — e ia
    // parar na tela de WhatsApp de suporte em vez de voltar para o onboarding.
    const onboardingComecado = !!tenantConfig.onboarding_iniciado_em

    // Se o onboarding não foi completado, redirecionar para o onboarding
    if (!onboardingCompleted && (faltaFazerConfiguracoesIniciais || onboardingComecado)) {
        if (empresaData && !routeOnboarding) {
            return { name: 'onboarding' }
        }
    } else if (!onboardingCompleted) {
        // Empresas antigas que já configuraram mas não têm o campo onboarding_completed
        // Verificar apenas o WhatsApp de suporte
        const routeConfigurationWhatsappSuporte = ['initialConfigurationWhatsappSuporte', 'onboarding'].indexOf(to.name) !== -1
        const possuiWhatsappDeSuporteConfigurado = empresaData && empresaData.whatsapp_suporte

        if (empresaData && !routeConfigurationWhatsappSuporte && !possuiWhatsappDeSuporteConfigurado) {
            return { name: 'initialConfigurationWhatsappSuporte' }
        }
    }

    return null
}

const validateAuthRoute = async (to, from, next) => {
    const base = baseRoute();

    if (['agendamentoDetalheChecklist', 'agendamentoDetalhe', 'orcamentoChecklist'].includes(to.name) || (['agendamentoDetalheChecklist', 'agendamentoDetalhe', 'orcamentoChecklist'].includes(from.name) && base === 'cliente')) {
        await store.dispatch('auth/fetchAuthUserCliente')
        return next()
    }

    if (['site-home', 'catalogo-virtual', 'catalogo-virtual-agendamento', 'site-home-lavarapido', 'site-home-lavajato', 'site-home-esteticaautomotiva', 'site-home-mecanica', 'site-home-chapeacao', 'site-home-funilaria', 'site-home-borracharia'].indexOf(to.name) !== -1) {
        return next()
    }

    if (['login-client', 'login-company', 'selecionar-perfil', 'definir-senha', 'convite-vinculo', 'esqueciminhasenha-company', 'esqueciminhasenha-client', 'cadastrolead', 'cadastroempresa', 'buscador', 'termodeuso', 'politicaprivacidade', 'cadastro', 'excluir-conta', 'login-geral', 'blog', 'blog-details'].indexOf(to.name) !== -1) {
        // Saída explícita: só quem quer mesmo encerrar a sessão manda `?sair=1`
        // (botões "Sair" e "Limpar cache" da navbar). Sem essa flag, chegar na
        // tela de login NÃO pode destruir uma sessão válida — era o que
        // expulsava quem voltava de um app externo e caía no login-geral.
        // `login-client` continua encerrando a sessão incondicionalmente: a
        // restauração de boot frio é exclusiva da área da empresa, então aqui a
        // remoção da cláusula só deixaria sessão órfã viva em aparelho
        // compartilhado (o cliente seguinte cairia dentro da conta do anterior).
        // `String(...)` para que `query: { sair: 1 }` numérico também valha.
        const saidaExplicita = ehSaidaExplicita(to) || to.name === 'login-client'

        if ((saidaExplicita || store.getters['auth/authenticated']) && !ROTAS_SEM_LOGOUT.includes(to.name)) {
            ability.update(initialAbility);
            switch (base) {
                case 'empresa':
                    await store.dispatch('auth/logout')
                    break;
                case 'cliente':
                    await store.dispatch('auth/logoutCliente')
                    break;
            }

            // A saída foi de propósito: nada a restaurar daqui para a frente
            // nesta carga da página.
            //
            // Para a navbar e para o interceptor de 422 este marcador é
            // redundante — eles mandam `?sair=1`, que o
            // `podeRestaurarNaEscolhaDePerfil` corta antes. Ele existe pelo
            // caminho que NÃO manda query nenhuma: os botões "Voltar ao Login"
            // de `NaoAutorizado.vue` e de `cliente/naoAutorizado/Index.vue`,
            // que apontam para `login-geral` com o usuário ainda autenticado.
            // Sem o marcador, a tela consultaria o servidor ao montar e a
            // sessão recém-encerrada apareceria viva caso o `logout` acima
            // tivesse falhado na rede.
            marcarSessaoEncerrada()
        } else {
            // Boot frio: `userData` está vazio, mas a sessão do servidor pode
            // continuar válida. Se estiver, devolve o usuário para o sistema em
            // vez de mostrar a tela de login.
            const restaurou = await restaurarSessaoSeExistir(to, from, base)
            if (restaurou) return next(restaurou)

            ability.update(initialAbility);
        }
    } else if (!store.getters['auth/authenticated']) {
        switch (base) {
            case 'empresa':
                await store.dispatch('auth/fetchAuthUser')
                break;
            case 'cliente':
                await store.dispatch('auth/fetchAuthUserCliente')
                break;
        }

        if (!store.getters['auth/authenticated']) {
            if (base === 'cliente') {
                return next({
                    name: 'login-client',
                    query: {
                        r: to.fullPath
                    }
                })
            }
            return next({
                name: 'login-company',
                query: {
                    r: to.fullPath
                }
            })
        } else {
            ability.update(store.getters['auth/abilities'])

            // Acabou de chegar com a store fria (recarga completa, que é como o
            // login entra): a configuração inicial vale aqui igual.
            if (base === 'empresa') {
                const destino = redirecionoDeConfiguracaoInicial(to)
                if (destino) return next(destino)
            }
        }
    } else if (base === 'empresa') {
        // Usuário já autenticado - garante que o auto-refresh das permissões do plano está ativo
        // e atualiza se estiverem expiradas
        try {
            const planModule = await getPlanPermissionsModule()
            planModule.startAutoRefresh()
            if (planModule.arePlanPermissionsExpired()) {
                planModule.fetchPlanPermissionsFromBackend()
            }
        } catch (e) {
            // Ignora erro se módulo não carregar
        }

        const destino = redirecionoDeConfiguracaoInicial(to)
        if (destino) return next(destino)
    }


    if (!canNavigate(to)) {
        // Primeiro verifica localmente se pode ser bloqueio de plano
        const planBlockInfoLocal = isRouteBlockedByPlan(to)

        if (planBlockInfoLocal) {
            // Tenta atualizar permissões e re-verificar em paralelo para não bloquear
            try {
                const [permissoesAtualizadas] = await Promise.all([
                    store.dispatch('auth/refreshPermissions'),
                    store.dispatch('auth/refreshPlanPermissions'),
                ])

                if (permissoesAtualizadas) {
                    ability.update(store.getters['auth/abilities'])

                    // Verifica novamente se pode navegar após atualização
                    if (canNavigate(to)) {
                        return next()
                    }
                }

                // Verifica novamente com a validação assíncrona (dupla checagem no backend)
                const planBlockInfoAsync = await isRouteBlockedByPlanAsync(to)

                if (!planBlockInfoAsync) {
                    // Não é bloqueio de plano, provavelmente é bloqueio de perfil
                    return next({
                        name: base === 'cliente' ? 'naoAutorizado-client' : 'naoAutorizado'
                    })
                }

                // Confirmado: é bloqueio de plano - mostra swal de upgrade
                showPlanBlockSwal(planBlockInfoAsync)
                return next(false)
            } catch (error) {
                console.error('Erro ao verificar permissões:', error)
                showPlanBlockSwal(planBlockInfoLocal)
                return next(false)
            }
        }

        // Não é bloqueio de plano, é bloqueio de perfil ou outra razão
        return next({
            name: base === 'cliente' ? 'naoAutorizado-client' : 'naoAutorizado'
        })
    }

    return next();
}

export { validateAuthRoute, storeModule, resolverDestinoSessaoAtiva, podeRestaurarNaEscolhaDePerfil };
